import { PlaceholderImage, Skeleton, Typography } from "@/components/atoms";
import type { TypographyVariant } from "@/components/atoms/Typography";
import { BackArrowIcon } from "@/components/atoms/BackArrowIcon";
import {
  BuildingOutlineIcon,
  DownloadIcon,
  FileIcon,
  LocationPinIcon,
  MapPinnedIcon,
} from "@/components/atoms/icons";
import {
  BottomSheet,
  CancelAppointmentDialog,
  FullScreenLoader,
  OkDialog,
  Accordion,
} from "@/components/molecules";
import { ImageCarousel } from "@/components/molecules/ImageCarousel";
import { Toast } from "@/components/molecules/Toast";
import type { UIColorTokens } from "@/core/constants/uiColors";
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from "@/core/constants/dynatraceCatalog";
import { useCatalogDynatraceView } from "@/core/hooks/useCatalogDynatraceView";
import { useContentfulRevision } from "@/core/hooks/useContentfulRevision";
import { trackCatalogEvent, trackProjectPromoBannerClick } from "@/core/services/trackDynatraceCatalog";
import {
  // useSalesAgentContact, // Sales agent contact hidden for now
  useSingleTapAction,
  useTranslation,
  useUIColors,
} from "@/core/hooks";
import { logger } from "@/core/logger";
import { useI18nContext } from "@/core/providers/I18nProvider";
import {
  appointmentService,
  extractUpcomingAppointment,
} from "@/core/api/services/appointmentService";
import {
  fetchPropertyAssets,
  type ExploreVrBannerData,
  type PropertyImage,
} from "@/core/services/contentfulClient";
import { useAppStore } from "@/core/stores/appStore";
import { downloadAndPersistBrochureFiles } from "@/core/utils/brochureDownload";
import { shareFile } from "@/core/wrappers/sharing";
import { useAppointmentLabels } from "@/modules/book-appointment/hooks/useAppointmentLabels";
import { ExploreVrBanner } from "@/modules/properties-web/components/ExploreVrBanner";
import {
  getUnitDetailsLabels,
  type PropertyTranslateFn,
} from "@/modules/properties-web/constants/getPropertyLabels";
import {
  getGuestDetailsLayout,
  getGuestDetailsTypeScale,
} from "@/modules/guest-web/constants/guestLayout";
import { useGuestLabels } from "@/modules/guest-web/hooks/useGuestLabels";
import { GuestUnitAboutSections } from "@/modules/guest-web/components/GuestUnitAboutSections";
import type { GuestUnitDetailsModel, GuestUnitDocument } from "@/modules/guest-web/types/guest.types";
import { useMobileAmenities } from "@/modules/property-details/hooks/useMobileAmenities";
import type { WebAmenity } from "@/modules/property-details/hooks/useWebAmenities";
import { WebAmenityIcon } from "@/modules/property-details/components/WebAmenityIcon";
import { AppointmentStatusCard } from "./components/AppointmentStatusCard";
import { useAvailableUnits } from "@/modules/properties-web/hooks/useAvailableUnits";
import { router } from "expo-router";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
  type ImageSourcePropType,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const AMENITIES_PER_ROW = 3;
/** Ignore near-zero coordinates the API sometimes sends instead of omitting the fields. */
const HAS_COORDINATES_EPSILON = 0;

interface AdhaProjectDetailsModel extends GuestUnitDetailsModel {
  readonly exploreVrBanner: ExploreVrBannerData | null;
  /** Master Plan card — same banner shape/look as the Explore VR one. */
  readonly masterPlanBanner: ExploreVrBannerData | null;
}

/** All brochure URLs from Contentful; falls back to the single `brochureUrl` field. */
function resolveBrochureUrls(assets: Readonly<{
  brochureUrls?: string[];
  brochureUrl?: string;
}>): string[] {
  if (assets.brochureUrls && assets.brochureUrls.length > 0) {
    return assets.brochureUrls;
  }
  if (assets.brochureUrl) {
    return [assets.brochureUrl];
  }
  return [];
}

function resolveConstructionStatusBadge(
  rawCompletion: string | number | null | undefined,
  template: string,
): string | undefined {
  if (rawCompletion == null || rawCompletion === "") {
    return undefined;
  }
  const parsed = Number.parseFloat(String(rawCompletion));
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return undefined;
  }
  const percent = Math.min(100, Math.max(0, parsed));
  return template.replace("{percent}", String(percent));
}

function buildProjectMapsUrl(params: Readonly<{
  platformOs: string;
  latitude: number;
  longitude: number;
}>): string {
  if (params.platformOs === "ios") {
    return `http://maps.apple.com/?q=${params.latitude},${params.longitude}`;
  }
  if (params.platformOs === "android") {
    return `geo:${params.latitude},${params.longitude}?q=${params.latitude},${params.longitude}`;
  }
  return `https://www.google.com/maps/search/?api=1&query=${params.latitude},${params.longitude}`;
}

/**
 * Fetches Contentful project assets and applies them to state — extracted
 * from the component (Sonar S3776: the nested .then/.catch/.finally guards
 * pushed the component's cognitive complexity over budget).
 */
async function loadProjectAssets(params: Readonly<{
  projectId: string;
  locale: "en" | "ar";
  isCancelled: () => boolean;
  setContentfulImages: (images: PropertyImage[]) => void;
  setContentfulName: (name: string | undefined) => void;
  setContentfulDescription: (description: string | undefined) => void;
  setContentfulBrochureUrls: (urls: string[]) => void;
  setContentfulMasterPlanLink: (link: string | undefined) => void;
  setExploreVrBanner: (banner: ExploreVrBannerData | null) => void;
  setContentfulMasterPlanBanner: (banner: ExploreVrBannerData | null) => void;
  setIsLoadingAssets: (loading: boolean) => void;
}>): Promise<void> {
  try {
    const assets = await fetchPropertyAssets(params.projectId, {
      maxWidth: 1200,
      locale: params.locale,
    });
    if (params.isCancelled()) {
      return;
    }
    params.setContentfulImages(assets.images);
    params.setContentfulName(assets.name?.trim() || undefined);
    params.setContentfulDescription(assets.description?.trim() || undefined);
    params.setContentfulBrochureUrls(resolveBrochureUrls(assets));
    params.setContentfulMasterPlanLink(assets.viewLink?.trim() || undefined);
    params.setExploreVrBanner(assets.exploreVrBanner ?? null);
    params.setContentfulMasterPlanBanner(assets.masterPlanBanner ?? null);
  } catch (error) {
    logger.warn("ProjectDetailsScreenAdhaMobile", "Failed to fetch Contentful project assets", { error });
    if (!params.isCancelled()) {
      params.setContentfulImages([]);
      params.setExploreVrBanner(null);
      params.setContentfulMasterPlanBanner(null);
    }
  } finally {
    if (!params.isCancelled()) {
      params.setIsLoadingAssets(false);
    }
  }
}

/**
 * Downloads the brochure and applies the success/error dialog state —
 * extracted from the component (Sonar S3776) alongside loadProjectAssets.
 */
async function downloadProjectBrochure(params: Readonly<{
  urls: readonly string[];
  title: string;
  t: PropertyTranslateFn;
  setSuccessMessage: (message: string) => void;
  setSavedFileUri: (uri: string | null) => void;
  setSavedRequiresShare: (value: boolean) => void;
  setSuccessDialogVisible: (visible: boolean) => void;
  setErrorMessage: (message: string) => void;
  setErrorDialogVisible: (visible: boolean) => void;
}>): Promise<void> {
  try {
    const result = await downloadAndPersistBrochureFiles(params.urls, params.title, params.t as any);
    if (result.success && result.fileUri && result.message) {
      params.setSuccessMessage(result.message);
      params.setSavedFileUri(result.fileUri);
      params.setSavedRequiresShare(Boolean(result.requiresShareToSave));
      params.setSuccessDialogVisible(true);
      return;
    }
    params.setErrorMessage(result.error || params.t("propertyDetails.documentSaveFailed"));
    params.setErrorDialogVisible(true);
  } catch (err: any) {
    logger.error("ProjectDetailsScreenAdhaMobile", "Failed to download brochure", {
      error: err?.message || err,
    });
    params.setErrorMessage(err?.message || params.t("propertyDetails.brochureDownloadFailed"));
    params.setErrorDialogVisible(true);
  }
}

// Memoized: loops over every amenity to build rows — this screen's other
// useState toggles (e.g. the appointment bottom sheet) shouldn't force that
// loop to redo on every render when the amenities themselves haven't changed.
const AmenityRows = React.memo(function AmenityRows({
  amenities,
  iconColor,
  labelVariant,
}: Readonly<{
  amenities: readonly WebAmenity[];
  iconColor: string;
  labelVariant: TypographyVariant;
}>) {
  const rows: React.JSX.Element[] = [];
  for (let start = 0; start < amenities.length; start += AMENITIES_PER_ROW) {
    const rowAmenities = amenities.slice(start, start + AMENITIES_PER_ROW);
    rows.push(
      <View key={rowAmenities[0]?.value ?? `amenity-row-${start}`} style={styles.amenitiesRow}>
        {rowAmenities.map(({ value, label, iconUrl }) => (
          <View key={value} style={styles.amenityItem}>
            <WebAmenityIcon iconUrl={iconUrl} label={label} size={24} defaultColor={iconColor} />
            <Typography variant={labelVariant} color="secondary" style={styles.amenityText}>
              {label}
            </Typography>
          </View>
        ))}
      </View>,
    );
  }
  return <>{rows}</>;
});

/**
 * Hero carousel + status badge, then title / location / description / map pin —
 * extracted from the component (Sonar S3776) since its own conditionals were
 * pushing the parent's cognitive complexity over budget.
 */
function ProjectHeroAndTitle({
  images,
  carouselHeight,
  autoScrollPaused,
  statusBadge,
  title,
  location,
  description,
  hasCoordinates,
  onMapPress,
  mapIconSize,
  isRTL,
  colors,
}: Readonly<{
  images: ImageSourcePropType[];
  carouselHeight: number;
  autoScrollPaused: boolean;
  statusBadge: string | undefined;
  title: string;
  location: string | undefined;
  description: string | undefined;
  hasCoordinates: boolean;
  onMapPress: () => void;
  mapIconSize: number;
  isRTL: boolean;
  colors: UIColorTokens;
}>) {
  const trimmedDescription = description?.trim();
  return (
    <>
      <View style={styles.hero}>
        {images.length > 0 ? (
          <ImageCarousel
            images={images}
            height={carouselHeight}
            activeDotColor={colors.white}
            autoScrollPaused={autoScrollPaused}
          />
        ) : (
          <View
            style={[
              styles.heroPlaceholder,
              { height: carouselHeight, backgroundColor: colors.backgroundLevel2 },
            ]}
          >
            <PlaceholderImage />
          </View>
        )}
        {statusBadge ? (
          <View
            style={[styles.constructionBadge, { [isRTL ? "right" : "left"]: 12 }]}
            pointerEvents="none"
          >
            <Typography variant="caption" color="primary" style={styles.constructionBadgeText}>
              {statusBadge}
            </Typography>
          </View>
        ) : null}
      </View>

      <View style={styles.titleBlock}>
        <View style={styles.titleRow}>
          <View style={styles.titleTextGroup}>
            <Typography variant="title2" color="primary" style={styles.titleText}>
              {title}
            </Typography>
            {location ? (
              <View style={styles.locationRow}>
                <LocationPinIcon size={14} color={colors.fontTertiary} />
                <Typography variant="footnote" color="secondary">
                  {location}
                </Typography>
              </View>
            ) : null}
            {trimmedDescription ? (
              <Typography variant="caption" color="secondary">
                {trimmedDescription}
              </Typography>
            ) : null}
          </View>
          {hasCoordinates ? (
            <Pressable onPress={onMapPress} accessibilityRole="button">
              <MapPinnedIcon size={mapIconSize} color={colors.fontPrimary} />
            </Pressable>
          ) : null}
        </View>
      </View>
    </>
  );
}

/**
 * Brochure document row in its white card — extracted from the component
 * (Sonar S3776) since its own conditionals were pushing the parent's
 * cognitive complexity over budget.
 *
 * Memoized — onDownloadBrochure is useCallback'd so this skips re-rendering
 * when unrelated screen state (e.g. the appointment bottom sheet) toggles.
 */
const ProjectDocumentsSection = React.memo(function ProjectDocumentsSection({
  brochureDocument,
  isDownloadingBrochure,
  onDownloadBrochure,
  colors,
}: Readonly<{
  brochureDocument: GuestUnitDocument | undefined;
  isDownloadingBrochure: boolean;
  onDownloadBrochure: () => void;
  colors: UIColorTokens;
}>) {
  if (!brochureDocument) {
    return null;
  }
  return (
    <View style={[styles.documentsSection, { backgroundColor: colors.backgroundLevel1 }]}>
      <View style={styles.documentRowWrap}>
        <Pressable
          style={[
            styles.documentRow,
            { backgroundColor: colors.backgroundLevel1, borderColor: colors.backgroundLevel4 },
          ]}
          onPress={() => !isDownloadingBrochure && onDownloadBrochure()}
          accessibilityRole="button"
        >
          <FileIcon size={24} color={colors.fontPrimary} />
          <Typography variant="body" color="primary" style={styles.documentRowTitle}>
            {brochureDocument.title}
          </Typography>
          <DownloadIcon size={24} color={colors.fontPrimary} />
        </Pressable>
        {isDownloadingBrochure ? (
          <View style={[styles.loadingOverlay, { backgroundColor: colors.backgroundLevel1 }]}>
            <ActivityIndicator size="small" color={colors.fontPrimary} />
          </View>
        ) : null}
      </View>
    </View>
  );
});

/**
 * Book Appointment sticky footer — same bottom-bar look as the unit detail
 * screen's PropertyDetailsAdhaStickyFooter, just a single full-width action
 * (no reserve CTA at project level). Extracted from the component (Sonar
 * S3776) alongside ProjectDocumentsSection.
 */
function ProjectBookAppointmentFooter({
  bottomInset,
  label,
  onPress,
  colors,
}: Readonly<{
  bottomInset: number;
  label: string;
  onPress: () => void;
  colors: UIColorTokens;
}>) {
  return (
    <View
      style={[
        styles.stickyFooterWrap,
        { backgroundColor: Platform.OS === "ios" ? "transparent" : colors.backgroundLevel1 },
      ]}
    >
      <View
        style={[
          styles.bottomBar,
          { backgroundColor: colors.backgroundLevel1, paddingBottom: 20 + bottomInset },
        ]}
      >
        <Pressable
          style={[styles.bottomActionButton, { backgroundColor: colors.buttonDefault }]}
          onPress={onPress}
          accessibilityRole="button"
        >
          <Typography
            variant="headline"
            color="white"
            numberOfLines={1}
            allowFontScaling={false}
            style={styles.bottomActionButtonText}
          >
            {label}
          </Typography>
        </Pressable>
      </View>
    </View>
  );
}

function resolveAdhaProjectIdentity(
  projectDetails: ReturnType<typeof useAppStore.getState>['adhaProjectDetails'],
  loanDetails: ReturnType<typeof useAppStore.getState>['adhaLoanDetails'],
): { projectId: string; projectName: string; projectLocation: string } {
  return {
    projectId: projectDetails?.project_id || loanDetails?.projectId || "",
    projectName: projectDetails?.project_name || loanDetails?.projectName || "",
    projectLocation:
      projectDetails?.project_location || loanDetails?.projectLocation || "",
  };
}

/**
 * ADHA "Project Details" — opened from the home screen's project hero card.
 * Mirrors GuestUnitDetailsScreenMobile.tsx's mobile shell (header, hero,
 * Accordion + local 3-col amenities grid) — deliberately NOT the web
 * AdhaListingProjectPanel/WebAccordion pair, which are desktop-styled.
 * Data: `adhaProjectDetails` (raw customer/profile API, set once at ADHA
 * login — same store field web's PropertiesListScreen reads) for
 * amenities/completion/coordinates, plus a fresh Contentful fetch for
 * images/description/VR banner/brochure/master-plan — same shape
 * PropertiesListScreen.tsx's buildAdhaGuestProjectDetails builds, adapted
 * locally since that function is private to a web-owned file.
 */
export function ProjectDetailsScreenAdhaMobile() {
  const colors = useUIColors();
  const { t, currentLanguage } = useTranslation();
  const { isRTL } = useI18nContext();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const layout = useMemo(() => getGuestDetailsLayout(width), [width]);
  const typeScale = useMemo(() => getGuestDetailsTypeScale(layout), [layout]);
  const labels = useGuestLabels();
  const unitLabels = useMemo(
    () => getUnitDetailsLabels(t as PropertyTranslateFn),
    [t],
  );

  const adhaProjectDetails = useAppStore((s) => s.adhaProjectDetails);
  const adhaLoanDetails = useAppStore((s) => s.adhaLoanDetails);
  const {
    projectId,
    projectName: projectNameFromProfile,
    projectLocation: projectLocationFromProfile,
  } = resolveAdhaProjectIdentity(adhaProjectDetails, adhaLoanDetails);
  useCatalogDynatraceView(
    ALMETLAA_SCREEN.PROJECT,
    projectId ? ALMETLAA_ACTION.PROJECT.detailPage(projectId) : '',
    Boolean(projectId),
  );

  const handleMasterPlanBannerPress = React.useCallback(() => {
    trackProjectPromoBannerClick({
      projectId,
      projectName: projectNameFromProfile,
      kind: "master_plan",
    });
  }, [projectId, projectNameFromProfile]);

  const handleExploreVrBannerPress = React.useCallback(() => {
    trackProjectPromoBannerClick({
      projectId,
      projectName: projectNameFromProfile,
      kind: "explore_vr",
    });
  }, [projectId, projectNameFromProfile]);

  // Same "Book Appointment" FAB as the home screen's Exclusive Listings
  // section — once the user already has one booked, the button becomes
  // "View Appointment" instead of disappearing (see AppointmentStatusCard
  // bottom sheet below), and (since this screen is project-level, not a
  // specific unit) targets the project's first catalogue unit as a stand-in,
  // same as home's primaryUnitId.
  const appointment = useAppStore((s) => s.appointment);
  const hasConfirmedAppointment = appointment != null;
  const { data: availableUnitsData } = useAvailableUnits(projectId);
  const primaryUnitId = availableUnitsData?.units?.[0]?.unit_id ?? "";
  const appointmentLabels = useAppointmentLabels();
  // const salesAgent = useSalesAgentContact(); // Sales agent contact hidden for now

  // Appointment view/cancel/reschedule state — same shell as
  // PropertyDetailsScreenAdhaMobile's "View Appointment" bottom sheet.
  const [bottomSheetVisible, setBottomSheetVisible] = useState(false);
  const [cancelAppointmentVisible, setCancelAppointmentVisible] = useState(false);
  const [isCancelingAppointment, setIsCancelingAppointment] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  // Holds a navigation/dialog-open to run once the bottom sheet's Modal has
  // actually finished closing (iOS) — opening another Modal right after
  // onClose races with the dismiss animation and gets swallowed.
  const pendingSheetActionRef = useRef<(() => void) | null>(null);

  // Pauses the hero ImageCarousel's auto-scroll timer while this page's own
  // ScrollView is actively being dragged — see the matching comment in
  // PropertyDetailsScreenAdhaMobile.tsx for why.
  const [isPageScrolling, setIsPageScrolling] = useState(false);
  const pageScrollSettleRef = useRef<any>(null);
  const handlePageScrollBeginDrag = useCallback(() => {
    if (pageScrollSettleRef.current) {
      clearTimeout(pageScrollSettleRef.current);
    }
    setIsPageScrolling(true);
  }, []);
  const handlePageScrollSettled = useCallback(() => {
    if (pageScrollSettleRef.current) {
      clearTimeout(pageScrollSettleRef.current);
    }
    setIsPageScrolling(false);
  }, []);
  const handlePageScrollEndDrag = useCallback(() => {
    if (pageScrollSettleRef.current) {
      clearTimeout(pageScrollSettleRef.current);
    }
    pageScrollSettleRef.current = setTimeout(() => {
      setIsPageScrolling(false);
    }, 400);
  }, []);

  const [contentfulImages, setContentfulImages] = useState<PropertyImage[]>([]);
  const [contentfulName, setContentfulName] = useState<string | undefined>();
  const [contentfulDescription, setContentfulDescription] = useState<string | undefined>();
  const [contentfulBrochureUrls, setContentfulBrochureUrls] = useState<string[]>([]);
  const [contentfulMasterPlanLink, setContentfulMasterPlanLink] = useState<string | undefined>();
  const [exploreVrBanner, setExploreVrBanner] = useState<ExploreVrBannerData | null>(null);
  const [contentfulMasterPlanBanner, setContentfulMasterPlanBanner] =
    useState<ExploreVrBannerData | null>(null);
  const [isLoadingAssets, setIsLoadingAssets] = useState(true);
  // Reload CMS content when the app-wide Contentful refresh runs (app foreground).
  const contentfulRevision = useContentfulRevision();

  useEffect(() => {
    let cancelled = false;
    if (!projectId) {
      setIsLoadingAssets(false);
      return undefined;
    }
    setIsLoadingAssets(true);
    void loadProjectAssets({
      projectId,
      locale: currentLanguage === "ar" ? "ar" : "en",
      isCancelled: () => cancelled,
      setContentfulImages,
      setContentfulName,
      setContentfulDescription,
      setContentfulBrochureUrls,
      setContentfulMasterPlanLink,
      setExploreVrBanner,
      setContentfulMasterPlanBanner,
      setIsLoadingAssets,
    });
    return () => {
      cancelled = true;
    };
  }, [projectId, currentLanguage, contentfulRevision]);

  const project: AdhaProjectDetailsModel | null = useMemo(() => {
    if (!projectId) {
      return null;
    }

    const images = contentfulImages
      .map((img) => ({ uri: img.url?.trim() ?? "" }))
      .filter((img) => Boolean(img.uri));

    const lat = adhaProjectDetails?.latitude;
    const lng = adhaProjectDetails?.longitude;
    const hasCoords =
      typeof lat === "number" &&
      Number.isFinite(lat) &&
      lat !== HAS_COORDINATES_EPSILON &&
      typeof lng === "number" &&
      Number.isFinite(lng) &&
      lng !== HAS_COORDINATES_EPSILON;

    const statusBadge = resolveConstructionStatusBadge(
      adhaProjectDetails?.completion_percentage,
      unitLabels.CONSTRUCTION_COMPLETED,
    );

    const brochureUrls = contentfulBrochureUrls.filter(Boolean);
    const documents: GuestUnitDetailsModel["documents"] = brochureUrls.length > 0
      ? [{
          id: "contentful-brochure",
          title: unitLabels.BROCHURE,
          url: brochureUrls[0] ?? "",
          urls: brochureUrls,
          action: "download",
        }]
      : [];

    const title = contentfulName?.trim() || projectNameFromProfile;

    // Prefer the Contentful `masterPlan` banner entry (copy + image + link).
    // Projects that only have the older `viewLink` field get local copy and
    // ExploreVrBanner's solid dark fill instead.
    const masterPlanLink = contentfulMasterPlanLink?.trim() ?? "";
    const fallbackMasterPlanBanner: ExploreVrBannerData | null = masterPlanLink
      ? {
          label: unitLabels.MASTER_PLAN,
          // `{{projectName}}` is filled in by ExploreVrBanner's projectName prop.
          title: t("propertyDetails.discoverMasterPlan"),
          buttonName: unitLabels.VIEW,
          startTourLink: masterPlanLink,
        }
      : null;
    const masterPlanBanner = contentfulMasterPlanBanner ?? fallbackMasterPlanBanner;

    return {
      id: projectId,
      title,
      location: projectLocationFromProfile.trim(),
      description: (contentfulDescription ?? "").trim(),
      images,
      typeTags: [],
      amenities: adhaProjectDetails?.amenities ?? [],
      exploreVrBanner,
      masterPlanBanner,
      ...(documents.length > 0 ? { documents } : {}),
      ...(hasCoords ? { latitude: lat, longitude: lng } : {}),
      ...(statusBadge ? { statusBadge } : {}),
    };
  }, [
    projectId,
    contentfulImages,
    contentfulName,
    contentfulDescription,
    contentfulBrochureUrls,
    contentfulMasterPlanLink,
    contentfulMasterPlanBanner,
    exploreVrBanner,
    projectNameFromProfile,
    projectLocationFromProfile,
    adhaProjectDetails?.latitude,
    adhaProjectDetails?.longitude,
    adhaProjectDetails?.completion_percentage,
    adhaProjectDetails?.amenities,
    unitLabels,
    t,
  ]);

  const isLoading = isLoadingAssets && !project?.images.length;

  const projectImages = useMemo<ImageSourcePropType[]>(() => {
    const images = project?.images ?? [];
    return images.map((img) => ({ uri: img.uri }));
  }, [project?.images]);

  const amenityItems = useMobileAmenities(project?.amenities);
  const hasAmenities = amenityItems.length > 0;
  const showAboutAccordion = Boolean(project && hasAmenities);
  // `{ ...project, amenities: [] }` inline in JSX would create a new object
  // every render (even though `project` itself is stable), defeating
  // GuestUnitAboutSections' React.memo below.
  const guestAboutUnit = useMemo(
    () => (project ? { ...project, amenities: [] } : null),
    [project],
  );

  // Rapid repeat taps would otherwise push the same screen / open the same
  // external link several times.
  const runSingleTap = useSingleTapAction();

  const handleMapPress = () => {
    if (project?.latitude == null || project?.longitude == null) {
      return;
    }
    const mapUrl = buildProjectMapsUrl({
      platformOs: Platform.OS,
      latitude: project.latitude,
      longitude: project.longitude,
    });
    runSingleTap(() => {
      Linking.openURL(mapUrl).catch((err) => {
        logger.error("ProjectDetailsScreenAdhaMobile", "Failed to open maps", err);
      });
    });
  };

  const [isDownloadingBrochure, setIsDownloadingBrochure] = useState(false);
  const [errorDialogVisible, setErrorDialogVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successDialogVisible, setSuccessDialogVisible] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [savedFileUri, setSavedFileUri] = useState<string | null>(null);
  const [savedRequiresShare, setSavedRequiresShare] = useState(false);
  const brochureDocument = project?.documents?.find((doc) => doc.action !== "view");

  // useCallback so ProjectDocumentsSection's React.memo below isn't defeated
  // by a fresh closure every render.
  // Single-tap guarded too: isDownloadingBrochure is state, so two quick taps
  // could both read it as false before the re-render and start two downloads.
  const handleDownloadBrochure = useCallback(() => {
    if (!brochureDocument || brochureDocument.urls.length === 0) {
      return;
    }
    runSingleTap(() => {
      if (primaryUnitId) {
        trackCatalogEvent({
          screen: ALMETLAA_SCREEN.UNIT,
          actionName: ALMETLAA_ACTION.UNIT.BROCHURE_DOWNLOAD_TEMPLATE.replace(
            "{unitId}",
            primaryUnitId,
          ),
          eventType: "action",
        });
      }
      void (async () => {
        setIsDownloadingBrochure(true);
        await downloadProjectBrochure({
          urls: brochureDocument.urls,
          title: project?.title || "Property",
          t: t as PropertyTranslateFn,
          setSuccessMessage,
          setSavedFileUri,
          setSavedRequiresShare,
          setSuccessDialogVisible,
          setErrorMessage,
          setErrorDialogVisible,
        });
        setIsDownloadingBrochure(false);
      })();
    });
  }, [brochureDocument, primaryUnitId, project?.title, runSingleTap, t]);

  const handleBookAppointmentPress = () => {
    if (hasConfirmedAppointment) {
      setBottomSheetVisible(true);
      return;
    }
    runSingleTap(() => {
      if (primaryUnitId) {
        trackCatalogEvent({
          screen: ALMETLAA_SCREEN.UNIT,
          actionName: ALMETLAA_ACTION.UNIT.bookAppointmentClick(primaryUnitId),
          eventType: "action",
        });
      }
      router.push({
        pathname: "/(auth)/book-appointment",
        params: { propertyId: primaryUnitId, mode: "book" },
      } as any);
    });
  };

  // Same iOS Modal-dismiss-race handling as handleCancelAppointment below —
  // the bottom sheet's Modal needs to finish closing before another Modal
  // (or a navigation) is triggered on top of it.
  const handleOpenAppointmentFlow = () => {
    const navigate = () =>
      runSingleTap(() => {
        if (primaryUnitId) {
          trackCatalogEvent({
            screen: ALMETLAA_SCREEN.UNIT,
            actionName: ALMETLAA_ACTION.UNIT.bookAppointmentClick(primaryUnitId),
            eventType: "action",
          });
        }
        router.push({
          pathname: "/(auth)/book-appointment",
          params: {
            propertyId: primaryUnitId,
            eventId: appointment?.eventId ?? "",
            mode: "reschedule",
          },
        } as any);
      });

    if (Platform.OS === "ios") {
      pendingSheetActionRef.current = navigate;
      setBottomSheetVisible(false);
    } else {
      setBottomSheetVisible(false);
      navigate();
    }
  };

  const handleCancelAppointment = () => {
    const openCancelDialog = () => setCancelAppointmentVisible(true);

    if (Platform.OS === "ios") {
      pendingSheetActionRef.current = openCancelDialog;
      setBottomSheetVisible(false);
    } else {
      setBottomSheetVisible(false);
      openCancelDialog();
    }
  };

  // Same as PropertyDetailsScreenAdhaMobile's confirmCancel — resolve the
  // event_id (from the store, falling back to GET /upcoming) and actually
  // cancel on the backend before clearing local state.
  const handleConfirmCancelAppointment = async () => {
    setCancelAppointmentVisible(false);
    setIsCancelingAppointment(true);

    let eventId = appointment?.eventId?.trim() || "";
    if (!eventId) {
      try {
        const response = await appointmentService.getUpcoming({
          unitId: primaryUnitId,
        });
        const dto = extractUpcomingAppointment(response);
        const rawEventId = dto?.event_id;
        eventId = typeof rawEventId === "string" ? rawEventId.trim() : "";
      } catch (error) {
        logger.warn("ProjectDetailsScreenAdhaMobile:cancel upcoming refresh failed", error);
      }
    }

    if (!eventId) {
      logger.warn("ProjectDetailsScreenAdhaMobile:cancel aborted — no appointment event_id");
      setToastMessage(appointmentLabels.CANCEL_ERROR);
      setToastVisible(true);
      setIsCancelingAppointment(false);
      return;
    }

    try {
      await appointmentService.updateAppointment({
        action_type: "Cancelled",
        event_id: eventId,
        appointment_slot_id: "",
        reason: "Cancelled by customer",
      });
      useAppStore.getState().clearAppointment();
      setToastMessage(t("propertyDetails.appointmentCanceledSuccess"));
      setToastVisible(true);
    } catch (error) {
      logger.warn("ProjectDetailsScreenAdhaMobile:confirmCancelAppointment failed", error);
      setToastMessage(appointmentLabels.CANCEL_ERROR);
      setToastVisible(true);
    } finally {
      setIsCancelingAppointment(false);
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}>
      <View style={[styles.header, { paddingTop: insets.top + 8 }]}>
        <Pressable
          onPress={() => runSingleTap(() => router.back())}
          hitSlop={8}
          accessibilityRole="button"
        >
          <BackArrowIcon size={24} />
        </Pressable>
        <Typography
          variant="title3"
          color="primary"
          numberOfLines={1}
          style={styles.headerTitle}
        >
          {t("properties.projectDetails")}
        </Typography>
        <View style={styles.headerSideSlot} />
      </View>

      {isLoading && (
        <ScrollView contentContainerStyle={styles.content}>
          <Skeleton height={layout.carouselHeight} borderRadius={16} />
          <Skeleton height={24} width="70%" borderRadius={6} />
          <Skeleton height={16} width="50%" borderRadius={6} />
        </ScrollView>
      )}

      {!isLoading && project && (
        <ScrollView
          contentContainerStyle={[
            styles.content,
            {
              paddingBottom:
                (hasConfirmedAppointment ? 96 : 16) + insets.bottom,
            },
          ]}
          showsVerticalScrollIndicator={false}
          onScrollBeginDrag={handlePageScrollBeginDrag}
          onScrollEndDrag={handlePageScrollEndDrag}
          onMomentumScrollEnd={handlePageScrollSettled}
        >
          <ProjectHeroAndTitle
            images={projectImages}
            carouselHeight={layout.carouselHeight}
            autoScrollPaused={isPageScrolling}
            statusBadge={project.statusBadge}
            title={project.title}
            location={project.location}
            description={project.description}
            hasCoordinates={project.latitude != null && project.longitude != null}
            onMapPress={handleMapPress}
            mapIconSize={typeScale.mapIconSize}
            isRTL={isRTL}
            colors={colors}
          />

          {project.masterPlanBanner ? (
            <ExploreVrBanner
              banner={project.masterPlanBanner}
              projectName={project.title}
              compact
              alignTextToStart
              centerContent
              onStartTourPress={handleMasterPlanBannerPress}
            />
          ) : null}

          {project.exploreVrBanner ? (
            <ExploreVrBanner
              banner={project.exploreVrBanner}
              projectName={project.title}
              compact
              alignTextToStart
              centerContent
              onStartTourPress={handleExploreVrBannerPress}
            />
          ) : null}

          {showAboutAccordion ? (
            <Accordion icon={BuildingOutlineIcon} title={labels.aboutProperty} defaultExpanded>
              <GuestUnitAboutSections
                unit={guestAboutUnit!}
                layout={layout}
                typeScale={typeScale}
                labels={labels}
                hideUnitType
              />
              <View style={styles.amenitiesSection}>
                <Typography variant={typeScale.aboutHeading} color="primary">
                  {labels.amenities}
                </Typography>
                <View style={styles.amenitiesContainer}>
                  <AmenityRows
                    amenities={amenityItems}
                    iconColor={colors.fontPrimary}
                    labelVariant={typeScale.amenityLabel}
                  />
                </View>
              </View>
            </Accordion>
          ) : null}

          <ProjectDocumentsSection
            brochureDocument={brochureDocument}
            isDownloadingBrochure={isDownloadingBrochure}
            onDownloadBrochure={handleDownloadBrochure}
            colors={colors}
          />
        </ScrollView>
      )}

      {hasConfirmedAppointment ? (
        <ProjectBookAppointmentFooter
          bottomInset={insets.bottom}
          label={t("propertyDetails.viewAppointmentCta")}
          onPress={handleBookAppointmentPress}
          colors={colors}
        />
      ) : null}

      <OkDialog
        visible={errorDialogVisible}
        message={errorMessage}
        onOk={() => setErrorDialogVisible(false)}
      />

      <OkDialog
        visible={successDialogVisible}
        title={t("common.downloadSuccessTitle")}
        message={successMessage}
        okText={Platform.OS === "ios" || savedRequiresShare ? t("common.saveShare") : t("common.ok")}
        onOk={async () => {
          if ((Platform.OS === "ios" || savedRequiresShare) && savedFileUri) {
            const shareResult = await shareFile({ uri: savedFileUri });
            if (!shareResult.success && shareResult.error) {
              logger.warn("ProjectDetailsScreenAdhaMobile", "Sharing failed", {
                error: shareResult.error,
                fileUri: savedFileUri,
              });
            }
          }
          setSuccessDialogVisible(false);
          setSavedFileUri(null);
          setSavedRequiresShare(false);
        }}
      />

      <BottomSheet
        visible={bottomSheetVisible}
        onClose={() => setBottomSheetVisible(false)}
        dragHandleWidth={60}
        onDismiss={() => {
          const action = pendingSheetActionRef.current;
          pendingSheetActionRef.current = null;
          action?.();
        }}
      >
        {appointment && (
          <AppointmentStatusCard
            colors={colors}
            t={t}
            date={appointment.date}
            time={appointment.time}
            /* Sales agent contact hidden for now
            agentName={salesAgent.name || undefined}
            agentPhone={salesAgent.phone || undefined}
            */
            onCancelAppointment={handleCancelAppointment}
            onReschedule={handleOpenAppointmentFlow}
          />
        )}
      </BottomSheet>

      <CancelAppointmentDialog
        visible={cancelAppointmentVisible}
        title={t("propertyDetails.cancelAppointment")}
        message={t("bookAppointment.cancelMessage")}
        noText={t("common.no")}
        yesText={t("common.yes")}
        onNo={() => setCancelAppointmentVisible(false)}
        onYes={handleConfirmCancelAppointment}
      />

      <Toast
        visible={toastVisible}
        message={toastMessage}
        onHide={() => setToastVisible(false)}
      />
      <FullScreenLoader visible={isCancelingAppointment} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingBottom: 8,
    gap: 12,
  },
  headerTitle: {
    flex: 1,
    textAlign: "center",
  },
  headerSideSlot: {
    width: 24,
  },
  content: {
    padding: 16,
    gap: 16,
  },
  hero: {
    borderRadius: 16,
    overflow: "hidden",
  },
  heroPlaceholder: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  constructionBadge: {
    position: "absolute",
    top: 12,
    backgroundColor: "rgba(245, 245, 245, 0.9)",
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  constructionBadgeText: {
    fontWeight: "600",
  },
  titleBlock: {
    gap: 8,
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  titleTextGroup: {
    flex: 1,
    gap: 10,
  },
  titleText: {
    flexShrink: 1,
  },
  locationRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  amenitiesSection: {
    paddingVertical: 12,
    gap: 16,
  },
  amenitiesContainer: {
    gap: 20,
  },
  amenitiesRow: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  amenityItem: {
    flex: 1,
    alignItems: "center",
    gap: 16,
    paddingHorizontal: 4,
  },
  amenityText: {
    textAlign: "center",
  },
  // Wrapping container just adds the white background/padding around the
  // bordered brochure row.
  documentsSection: {
    borderRadius: 8,
    padding: 12,
    gap: 12,
  },
  documentRowWrap: {
    position: "relative",
  },
  documentRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 8,
    opacity: 0.8,
  },
  documentRowTitle: {
    flex: 1,
    fontSize: 16,
    fontWeight: "600",
  },
  stickyFooterWrap: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 100,
  },
  bottomBar: {
    paddingHorizontal: 16,
    paddingTop: 14,
    borderTopWidth: 1,
    borderTopColor: "#EDEDED",
  },
  bottomActionButton: {
    height: 48,
    borderRadius: 4,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 8,
    overflow: "hidden",
  },
  bottomActionButtonText: {
    width: "100%",
    fontWeight: "700",
    fontSize: 13,
    lineHeight: 16,
    textAlign: "center",
  },
});

export default ProjectDetailsScreenAdhaMobile;
